/**
 * Printer Error Handler
 * User-friendly error messages and error recovery utilities
 */

export type PrinterErrorType =
  | 'BLUETOOTH_PERMISSION_DENIED'
  | 'BLUETOOTH_NOT_ENABLED'
  | 'BLUETOOTH_NOT_SUPPORTED'
  | 'CONNECTION_FAILED'
  | 'PRINTER_OUT_OF_RANGE'
  | 'PAPER_JAM'
  | 'PAPER_OUT'
  | 'PRINT_FAILED'
  | 'DEVICE_NOT_FOUND'
  | 'UNKNOWN';

export interface PrinterError {
  type: PrinterErrorType;
  message: string;
  originalError?: unknown;
}

/**
 * Map raw errors to user-friendly messages
 */
export function getPrinterErrorMessage(error: unknown): string {
  const msg = error instanceof Error ? error.message : String(error);

  if (/permission|denied/i.test(msg)) return 'Bluetooth permission denied. Please allow Bluetooth access in app settings.';
  if (/bluetooth.*not.*enabled|enable bluetooth/i.test(msg)) return 'Bluetooth is off. Please turn on Bluetooth in device settings.';
  if (/not supported|unsupported/i.test(msg)) return 'Bluetooth printing is not supported on this device.';
  if (/failed to connect|connection failed|connect.*printer/i.test(msg)) return 'Could not connect to printer. Ensure it is on and in range.';
  if (/out of range|disconnected|connection lost/i.test(msg)) return 'Printer out of range. Please move closer and try again.';
  if (/paper|jam|out of paper/i.test(msg)) return 'Paper issue: check for paper jam or refill paper.';
  if (/failed to send|print.*failed/i.test(msg)) return 'Print failed. Please check printer and try again.';
  if (/device.*not found|no.*device/i.test(msg)) return 'No printer found. Please scan and select a printer.';

  return msg || 'An unexpected error occurred. Please try again.';
}

/**
 * Determine if error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  if (/permission|denied/i.test(msg)) return true; // User can grant permission
  if (/failed to connect|connection failed|timeout/i.test(msg)) return true;
  if (/out of range|disconnected/i.test(msg)) return true;
  if (/paper|jam/i.test(msg)) return true; // After fixing paper
  return false;
}
